import React, { useEffect, useRef, useState } from 'react';
import { Home, MapPin, Loader2 } from 'lucide-react';
import { useAddressSuggestions, buildAutocompleteKeyHandler } from './useAddressSuggestions';

// Variant-specific class bundles, kept at module scope so they're not
// re-computed on every render.
const VARIANT = {
  dark: {
    input:    'w-full pl-10 pr-9 py-3 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400/50 transition-colors',
    dropdown: 'absolute z-30 top-full left-0 right-0 mt-1.5 bg-[#1a0a0a] border border-red-500/20 rounded-lg shadow-2xl overflow-hidden',
    item:     (active) => `w-full text-left px-3 py-2.5 text-sm text-white/90 ${active ? 'bg-red-500/15' : 'hover:bg-red-500/10'} flex items-start gap-2`,
    icon:     'text-red-400/70',
  },
  light: {
    input:    'w-full pl-10 pr-9 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/25 focus:border-red-400',
    dropdown: 'absolute z-30 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden',
    item:     (active) => `w-full text-left px-3 py-2.5 text-sm text-slate-800 ${active ? 'bg-red-50' : 'hover:bg-slate-50'} flex items-start gap-2`,
    icon:     'text-slate-400',
  },
};

/**
 * AU-only address autocomplete using Geoapify.
 * Drop-in replacement for a plain <input> — works the same way,
 * just shows a suggestion dropdown as the user types.
 */
const AddressAutocomplete = ({
  value,
  onChange,
  required,
  placeholder = 'Start typing your address…',
  icon: Icon = Home,
  testid = 'address-autocomplete',
  variant = 'dark',
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const skipNextFetch = useRef(false);
  const { suggestions, loading, queueFetch, clear, keyMissing } = useAddressSuggestions();
  const v = VARIANT[variant] || VARIANT.dark;

  // Close suggestions on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Sync `open` with `suggestions` arriving from the hook
  useEffect(() => {
    if (suggestions.length > 0) {
      setOpen(true);
      setHighlight(-1);
    }
  }, [suggestions]);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    queueFetch(val);
  };

  const pick = (s) => {
    skipNextFetch.current = true;
    onChange(s.formatted);
    setOpen(false);
    clear();
  };

  const onKeyDown = buildAutocompleteKeyHandler({
    open, suggestions, highlight, setHighlight, setOpen, pick,
  });

  return (
    <div ref={wrapRef} className="relative">
      <Icon size={16} className={`absolute left-3.5 top-3.5 ${v.icon} pointer-events-none`} />
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        onFocus={() => suggestions.length && setOpen(true)}
        onKeyDown={onKeyDown}
        required={required}
        placeholder={placeholder}
        autoComplete="street-address"
        className={v.input}
        data-testid={testid}
      />
      {loading && (
        <Loader2 size={14} className={`absolute right-3 top-3.5 animate-spin ${v.icon}`} />
      )}

      {open && suggestions.length > 0 && (
        <div className={v.dropdown} data-testid={`${testid}-dropdown`}>
          {suggestions.map((s, i) => (
            <button
              key={`${s.lat}|${s.lon}|${s.formatted}`}
              type="button"
              onClick={() => pick(s)}
              onMouseEnter={() => setHighlight(i)}
              className={v.item(i === highlight)}
              data-testid={`${testid}-item-${i}`}
            >
              <MapPin size={14} className={`flex-shrink-0 mt-0.5 ${v.icon}`} />
              <span className="leading-snug">{s.formatted}</span>
            </button>
          ))}
        </div>
      )}

      {keyMissing && (
        <p className="mt-1 text-[10px] text-amber-300/80">Address autocomplete is offline (missing API key).</p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
